import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import GetCookie from "../../utils/GetCookie";
import ProcessingPopup from "../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../utils/popup/ErrorPopup";
import SuccessPopup from "../../utils/popup/SuccessPopup";
import PaginationControls from "../../utils/PaginationControls";
import { useNavigate } from "react-router-dom";
import authNavigate from "../../globalComponents/AuthNav";

const VmMappingReviewModal = ({ isOpen, onClose, templateId, onConfirmSuccess, isPendingReview = false }) => {
  const [sections, setSections] = useState([]);
  const [allVms, setAllVms] = useState([]);
  const [sectionMappings, setSectionMappings] = useState({});
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Search, filter & pagination
  const [vmSearch, setVmSearch] = useState("");
  const [vmSearchInput, setVmSearchInput] = useState("");
  const [vmCategory, setVmCategory] = useState("");
  const [vmPage, setVmPage] = useState(1);
  const [vmSize, setVmSize] = useState(25);

  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

  const fetchReviewData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        "/pceapi/genbi/vm-mapping/review",
        { template_id: templateId },
        { headers }
      );
      const data = response.data;

      if (data.status === "NotAuthorized") {
        authNavigate("/login", "iam", navigate);
        return;
      }
      if (data.status === "failed") {
        setErrorMessage(data.message || "Failed to load review data");
        setShowError(true);
        return;
      }

      setSections(data.sections || []);
      setAllVms(data.value_messages || []);

      const mappings = {};
      (data.sections || []).forEach((s) => {
        mappings[s.section_id] = [...(s.mapped_vm_ids || [])];
      });
      setSectionMappings(mappings);

      // Auto-expand top-level and select first section
      const expanded = {};
      (data.sections || []).forEach((s) => {
        const depth = (s.section_ordering_id.match(/\./g) || []).length;
        if (depth === 0) expanded[s.section_id] = true;
      });
      setExpandedNodes(expanded);

      if (data.sections && data.sections.length > 0) {
        setSelectedSectionId(data.sections[0].section_id);
      }
    } catch (error) {
      setErrorMessage(error.message || "Failed to load review data");
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [templateId, headers, navigate]);

  useEffect(() => {
    if (isOpen && templateId) {
      fetchReviewData();
    }
  }, [isOpen, templateId, fetchReviewData]);

  // Reset filters when section changes
  useEffect(() => {
    setVmSearch("");
    setVmSearchInput("");
    setVmCategory("");
    setVmPage(1);
  }, [selectedSectionId]);

  // Build tree structure
  const sectionTree = useMemo(() => {
    if (!sections.length) return [];
    const sorted = [...sections].sort((a, b) =>
      a.section_ordering_id.localeCompare(b.section_ordering_id, undefined, { numeric: true })
    );
    const tree = [];
    const nodeMap = {};
    sorted.forEach((s) => {
      const node = { ...s, children: [] };
      nodeMap[s.section_ordering_id] = node;
      const parts = s.section_ordering_id.split(".");
      if (parts.length === 1) {
        tree.push(node);
      } else {
        const parentOrdering = parts.slice(0, -1).join(".");
        const parent = nodeMap[parentOrdering];
        if (parent) parent.children.push(node);
        else tree.push(node);
      }
    });
    return tree;
  }, [sections]);

  const categories = useMemo(() => {
    return [...new Set((allVms || []).map((vm) => vm.type_name).filter(Boolean))];
  }, [allVms]);

  const selectedSectionMappedIds = useMemo(() => {
    if (!selectedSectionId) return new Set();
    return new Set(sectionMappings[selectedSectionId] || []);
  }, [selectedSectionId, sectionMappings]);

  const connectedVms = useMemo(() => {
    return allVms.filter((vm) => selectedSectionMappedIds.has(vm.id));
  }, [allVms, selectedSectionMappedIds]);

  // Filter & paginate available (unconnected) VMs
  const filteredUnconnectedVms = useMemo(() => {
    let filtered = allVms.filter((vm) => !selectedSectionMappedIds.has(vm.id));
    if (vmSearch.trim()) {
      const searchTerm = vmSearch.toLowerCase();
      filtered = filtered.filter((vm) =>
        (vm.value_message || "").toLowerCase().includes(searchTerm) ||
        (vm.type_name || "").toLowerCase().includes(searchTerm) ||
        (vm.sub_types || []).some((st) => (st || "").toLowerCase().includes(searchTerm))
      );
    }
    if (vmCategory) {
      filtered = filtered.filter((vm) => vm.type_name === vmCategory);
    }
    return filtered;
  }, [allVms, selectedSectionMappedIds, vmSearch, vmCategory]);

  const paginatedUnconnectedVms = useMemo(() => {
    const start = (vmPage - 1) * vmSize;
    return filteredUnconnectedVms.slice(start, start + vmSize);
  }, [filteredUnconnectedVms, vmPage, vmSize]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredUnconnectedVms.length / vmSize));
  }, [filteredUnconnectedVms, vmSize]);

  const toggleVm = useCallback((vmId) => {
    if (!selectedSectionId) return;
    setSectionMappings((prev) => {
      const current = prev[selectedSectionId] || [];
      const updated = current.includes(vmId)
        ? current.filter((id) => id !== vmId)
        : [...current, vmId];
      return { ...prev, [selectedSectionId]: updated };
    });
  }, [selectedSectionId]);

  const toggleExpand = useCallback((sectionId) => {
    setExpandedNodes((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  const getTotalMappedCount = useCallback((sectionId) => {
    return (sectionMappings[sectionId] || []).length;
  }, [sectionMappings]);

  const handleVmSearch = () => {
    setVmSearch(vmSearchInput);
    setVmPage(1);
  };

  const handleVmKeyPress = (e) => {
    if (e.key === "Enter") handleVmSearch();
  };

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      const payload = sections.map((s) => ({
        section_id: s.section_id,
        vm_ids: sectionMappings[s.section_id] || [],
      }));

      const response = await axios.post(
        "/pceapi/genbi/vm-mapping/confirm",
        { template_id: templateId, section_mappings: payload },
        { headers }
      );
      const data = response.data;

      if (data.status === "NotAuthorized") {
        authNavigate("/login", "iam", navigate);
        return;
      }
      if (data.status === "failed") {
        setErrorMessage(data.message || "Failed to confirm mapping");
        setShowError(true);
        return;
      }

      setSuccessMessage("VM mapping saved successfully");
      setShowSuccess(true);
      setTimeout(() => {
        onConfirmSuccess && onConfirmSuccess();
        onClose();
      }, 1000);
    } catch (error) {
      setErrorMessage(error.message || "Failed to confirm mapping");
      setShowError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      await axios.post(
        "/pceapi/genbi/vm-mapping/reject",
        { template_id: templateId },
        { headers }
      );
    } catch (error) {
      setErrorMessage(error.message || "Failed to reject mapping");
      setShowError(true);
    } finally {
      setIsRejecting(false);
    }
    onConfirmSuccess && onConfirmSuccess();
    onClose();
  };

  const renderTreeNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.section_id];
    const isActive = selectedSectionId === node.section_id;
    const mappedCount = getTotalMappedCount(node.section_id);

    return (
      <div key={node.section_id}>
        <div
          className={`d-flex align-items-center py-1 px-2 rounded mb-1 ${isActive ? "ev-message-card1" : ""}`}
          style={{ paddingLeft: `${depth * 16 + 8}px`, cursor: "pointer" }}
          onClick={() => setSelectedSectionId(node.section_id)}
        >
          {hasChildren ? (
            <i
              className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"} me-1 ev-fs-11`}
              onClick={(e) => { e.stopPropagation(); toggleExpand(node.section_id); }}
              style={{ width: "16px", flexShrink: 0 }}
            ></i>
          ) : (
            <span style={{ width: "16px", flexShrink: 0 }}></span>
          )}
          <span className={`ev-fs-12 ${isActive ? "ev-bold-reg" : ""} flex-grow-1 text-truncate`}>
            {node.section_ordering_id}. {node.section_name}
          </span>
          {mappedCount > 0 && (
            <span className="badge gradient-bg ev-fs-10 ms-1" style={{ minWidth: "20px", flexShrink: 0 }}>
              {mappedCount}
            </span>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div>{node.children.map((child) => renderTreeNode(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const selectedSection = sections.find((s) => s.section_id === selectedSectionId);

  return (
    <>
      <div className="modal fade show d-block bg-dark bg-opacity-50" style={{ zIndex: 1050 }}>
        <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxHeight: "85vh" }}>
          <div className="modal-content ev-message-card2 ev-card-3 d-flex flex-column" style={{ maxHeight: "85vh" }}>

            {/* Header */}
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <div>
                <span className="ev-med-p-blue">
                  <i className="bi bi-diagram-3-fill me-1"></i>
                  View Section Mapping
                </span>
                <p className="ev-xsmall-p-dark mb-0 mt-1">
                  <i className="bi bi-info-circle me-1"></i>
                  Unmapped child sections inherit value messages from their closest mapped ancestor.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control search-input ms-0 ps-5"
                    style={{ minWidth: "260px" }}
                    placeholder="Search available messages"
                    value={vmSearchInput}
                    onChange={(e) => setVmSearchInput(e.target.value)}
                    onKeyPress={handleVmKeyPress}
                  />
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round position-absolute top-50 start-0 translate-middle-y ms-1"
                    onClick={handleVmSearch}
                  >
                    <i className="bi bi-search"></i>
                  </button>
                </div>
                <button
                  className="ev-btn-light-fill ev-btn-xsmall ev-radius-xlarge"
                  onClick={isPendingReview ? handleReject : onClose}
                  disabled={isRejecting}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
              {/* Left Panel - Section Tree */}
              <div className="ev-card-2 d-flex flex-column m-2" style={{ width: "260px", minWidth: "260px", borderRadius: "10px" }}>
                <div className="px-3 py-2">
                  <p className="ev-small-p-blue ev-bold-reg mb-0">
                    <i className="bi bi-list-nested me-1"></i> Sections
                  </p>
                </div>
                <div className="px-2 pb-2 scrollable-element flex-grow-1">
                  {sectionTree.map((node) => renderTreeNode(node, 0))}
                </div>
              </div>

              {/* Right Panel - VMs */}
              <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                {/* Section context header + category filter */}
                {selectedSection && (
                  <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                    <span className="ev-small-p-blue ev-bold-reg">
                      {selectedSection.section_ordering_id}. {selectedSection.section_name}
                    </span>
                    <div className="d-flex align-items-center">
                      <label className="ev-small-p-blue me-1 ev-fs-10">Category</label>
                      <select
                        className="ev-message-card2 ps-2 pe-2 pt-1 pb-1 ev-btn-small w-auto"
                        value={vmCategory}
                        onChange={(e) => { setVmCategory(e.target.value); setVmPage(1); }}
                      >
                        <option value="">All</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Scrollable VM content */}
                <div className="scrollable-element flex-grow-1 p-3">
                  {!selectedSectionId ? (
                    <div className="text-center mt-5">
                      <i className="bi bi-arrow-left-circle ev-fs-14 ev-xsmall-p-dark"></i>
                      <p className="ev-small-p-blue mt-2">Select a section to manage mappings</p>
                    </div>
                  ) : (
                    <>
                      {/* Connected VMs */}
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <span className="ev-small-p-blue ev-bold-reg">
                            <i className="bi bi-link-45deg me-1"></i> Connected
                          </span>
                          <span className="badge bg-primary ms-2 ev-fs-10">{connectedVms.length}</span>
                        </div>
                        {connectedVms.length > 0 ? (
                          <table className="table-custom1 w-100">
                            <tbody>
                              {connectedVms.map((vm) => (
                                <tr key={vm.id} className="ev-card-2 cursor-pointer" onClick={() => toggleVm(vm.id)}>
                                  <td className="align-top" style={{ width: "30px" }}>
                                    <div className="form-check mt-1">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={true}
                                        onChange={() => toggleVm(vm.id)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                  </td>
                                  <td className="align-top w-25">
                                    <p className="ev-reg-p-blue ms-1 mb-1">{vm.type_name}</p>
                                    {vm.sub_types && vm.sub_types.map((st, idx) => (
                                      <span key={idx} className="ev-message-card1 me-1 ev-btn-xsmall mt-1">{st}</span>
                                    ))}
                                  </td>
                                  <td className="align-top">
                                    <p className="ev-xsmall-p-dark mb-1">{vm.value_message}</p>
                                    {vm.ai_human_generated === "AI" && (
                                      <span className="badge gradient-bg ev-fs-10 ev-bold-reg">
                                        <i className="bi bi-stars"></i> AI
                                      </span>
                                    )}
                                  </td>
                                  <td className="align-top text-end" style={{ width: "40px" }}>
                                    <button
                                      className="btn btn-sm text-danger p-0"
                                      onClick={(e) => { e.stopPropagation(); toggleVm(vm.id); }}
                                      title="Disconnect"
                                    >
                                      <i className="bi bi-x-circle"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="ev-xsmall-p-dark ps-2">No value messages linked. Select from available below.</p>
                        )}
                      </div>

                      <hr className="my-2" />

                      {/* Available VMs */}
                      <div>
                        <div className="d-flex align-items-center mb-2">
                          <span className="ev-small-p-blue ev-bold-reg">
                            <i className="bi bi-plus-circle me-1"></i> Available
                          </span>
                          <span className="badge bg-secondary ms-2 ev-fs-10">{filteredUnconnectedVms.length}</span>
                        </div>
                        {paginatedUnconnectedVms.length > 0 ? (
                          <table className="table-custom1 w-100">
                            <tbody>
                              {paginatedUnconnectedVms.map((vm) => (
                                <tr key={vm.id} className="ev-card-2 cursor-pointer" onClick={() => toggleVm(vm.id)}>
                                  <td className="align-top" style={{ width: "30px" }}>
                                    <div className="form-check mt-1">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={false}
                                        onChange={() => toggleVm(vm.id)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                  </td>
                                  <td className="align-top w-25">
                                    <p className="ev-reg-p-blue ms-1 mb-1">{vm.type_name}</p>
                                    {vm.sub_types && vm.sub_types.map((st, idx) => (
                                      <span key={idx} className="ev-message-card1 me-1 ev-btn-xsmall mt-1">{st}</span>
                                    ))}
                                  </td>
                                  <td className="align-top">
                                    <p className="ev-xsmall-p-dark mb-1">{vm.value_message}</p>
                                    {vm.ai_human_generated === "AI" && (
                                      <span className="badge gradient-bg ev-fs-10 ev-bold-reg">
                                        <i className="bi bi-stars"></i> AI
                                      </span>
                                    )}
                                  </td>
                                  <td className="align-top text-end" style={{ width: "40px" }}>
                                    <button
                                      className="btn btn-sm text-primary p-0"
                                      onClick={(e) => { e.stopPropagation(); toggleVm(vm.id); }}
                                      title="Connect"
                                    >
                                      <i className="bi bi-plus-circle"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="ev-xsmall-p-dark ps-2">
                            {vmSearch || vmCategory ? "No matching value messages found" : "All value messages are linked"}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
              <div className="d-flex align-items-center gap-2">
                {filteredUnconnectedVms.length > 0 && (
                  <>
                    <button
                      className="ev-btn-light-fill ev-btn-xsmall"
                      disabled={vmPage === 1}
                      onClick={() => setVmPage(vmPage - 1)}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <span className="ev-xsmall-p-dark">
                      Page {vmPage}/{totalPages}
                    </span>
                    <button
                      className="ev-btn-light-fill ev-btn-xsmall"
                      disabled={vmPage >= totalPages}
                      onClick={() => setVmPage(vmPage + 1)}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                    <PaginationControls
                      initialSize={vmSize}
                      onSizeChange={(newSize) => {
                        setVmSize(newSize);
                        setVmPage(1);
                      }}
                    />
                    <span className="ev-xsmall-p-dark ms-2">
                      Showing {paginatedUnconnectedVms.length} of {filteredUnconnectedVms.length}
                    </span>
                  </>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="ev-btn-gradient-fill-round ev-bold-reg ev-btn-small"
                  onClick={handleConfirm}
                  disabled={isSaving || isRejecting}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  {isSaving ? "Saving..." : "Save Mappings"}
                </button>
                <button
                  className="ev-btn-red-fill-round ev-bold-reg ev-btn-small"
                  onClick={handleReject}
                  disabled={isSaving || isRejecting}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  {isRejecting ? "Rejecting..." : "Reject Mapping"}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {(isLoading || isSaving || isRejecting) && <ProcessingPopup message={isRejecting ? "Rejecting mapping..." : isSaving ? "Saving mapping..." : "Loading mapping data..."} />}
      {showError && <ErrorPopup message={errorMessage} onClose={() => setShowError(false)} />}
      {showSuccess && <SuccessPopup message={successMessage} onClose={() => setShowSuccess(false)} />}
    </>
  );
};

export default VmMappingReviewModal;
